// Lógica pura da tela Expedição (filtros, contagens, ordenação). Testes: tests/expedicao.logic.test.ts
import type { Alerta, Canal, ChaveColeta, DiaDeHoje, Faixa, LinhaColeta, Pedido, Situacao } from './types'
// .ts explícito: o node --test dos testes de lógica carrega este import em tempo de execução
import { CANAIS, ORDEM_COLETAS, ROTULO_CANAL, ROTULO_COLETA } from './types.ts'

export type Filtro = {
  canal: Set<Canal>
  faixa: Set<Faixa>
  situacao: Set<Situacao>
  statusMk: Set<string>
  /** embalador_id; pedido sem embalador usa a chave SEM_EMBALADOR. */
  embalador: Set<string>
  alerta: Set<Alerta>
  /** YYYY-MM-DD no fuso de São Paulo, comparado com a data SP do prazo. */
  de?: string
  ate?: string
}

export const SEM_EMBALADOR = 'sem'
export const SEM_STATUS_MK = 'sem'

export const FILTRO_INICIAL: Filtro = {
  canal: new Set(), faixa: new Set<Faixa>(['atrasado', 'hoje']), situacao: new Set(), statusMk: new Set(),
  embalador: new Set(), alerta: new Set(),
}

/** "Limpar filtros": nenhum filtro (diferente do inicial, que abre em Atrasados + Hoje). */
export const FILTRO_VAZIO: Filtro = {
  canal: new Set(), faixa: new Set(), situacao: new Set(), statusMk: new Set(), embalador: new Set(), alerta: new Set(),
}

/** Data (YYYY-MM-DD) em São Paulo (UTC-3, sem horário de verão) de um instante UTC ISO. */
export function diaSP(iso: string): string {
  return new Date(new Date(iso).getTime() - 3 * 3600_000).toISOString().slice(0, 10)
}

export function passa(p: Pedido, f: Filtro): boolean {
  if (f.canal.size && !f.canal.has(p.canal)) return false
  if (f.faixa.size && !f.faixa.has(p.faixa)) return false
  // Shopee Decora não tem situação do Tiny (a etapa vem do UpSeller): nunca passa num filtro de situação
  if (f.situacao.size && (p.etapa_upseller || !f.situacao.has(p.situacao))) return false
  if (f.statusMk.size && !f.statusMk.has(p.status_mk ?? SEM_STATUS_MK)) return false
  if (f.embalador.size && !f.embalador.has(p.embalador_id ?? SEM_EMBALADOR)) return false
  if (f.alerta.size && !p.alertas.some(a => f.alerta.has(a))) return false
  if (f.de || f.ate) {
    if (!p.prazo) return false
    const d = diaSP(p.prazo)
    if (f.de && d < f.de) return false
    if (f.ate && d > f.ate) return false
  }
  return true
}

export function contar(ps: Pedido[], por: 'situacao' | 'canal'): Record<string, number> {
  const out: Record<string, number> = {}
  for (const p of ps) out[String(p[por])] = (out[String(p[por])] ?? 0) + 1
  return out
}

/** Nada mais a fazer (regras.fechado do serviço): embalado (3) ou Shopee Decora enviada ou cancelada. A Decora com
 * "Etiqueta gerada" não está fechada: no UpSeller a etiqueta sai antes de embalar. */
export const fechado = (p: Pedido): boolean =>
  p.situacao === 3 || p.etapa_upseller === 'enviado' || p.etapa_upseller === 'cancelado'
/** Trabalho feito (regras.feito): embalado (3) ou Shopee Decora enviada. */
export const feito = (p: Pedido): boolean => p.situacao === 3 || p.etapa_upseller === 'enviado'

/** Faltam embalar: aguardando liberação (0), 1, 4 e 2, e a Decora a processar / com etiqueta (igual ao serviço). */
export const faltam = (ps: Pedido[]): Pedido[] => ps.filter(p => !fechado(p))

/** Não embalados primeiro; depois prazo crescente, sem prazo por último. */
export function ordenarLista(ps: Pedido[]): Pedido[] {
  return [...ps].sort((a, b) => {
    const ea = fechado(a) ? 1 : 0
    const eb = fechado(b) ? 1 : 0
    if (ea !== eb) return ea - eb
    if (a.prazo === b.prazo) return chavePedido(a) < chavePedido(b) ? -1 : chavePedido(a) > chavePedido(b) ? 1 : 0
    if (a.prazo === null) return 1
    if (b.prazo === null) return -1
    return a.prazo < b.prazo ? -1 : 1
  })
}

/** Chave do pedido no marketplace (canal + número). Várias separações com a mesma chave são volumes do mesmo pedido
 * (ex.: Amazon Onsite: uma separação, uma NF e uma etiqueta por unidade). Sem número, cada separação é um pedido.
 * A contagem do painel continua por separação (= volume = etiqueta); isto só identifica os volumes na lista. */
export const chavePedido = (p: Pedido): string => (p.pedido_marketplace ? `${p.canal}|${p.pedido_marketplace}` : `id|${p.id}`)

/** "22 pedidos · 33 volumes" quando algum pedido tem mais de uma separação; senão "33 pedidos" (1 = "1 pedido"). */
export function resumoPedidos(ps: Pedido[]): string {
  const n = new Set(ps.map(chavePedido)).size
  const txt = `${n} ${n === 1 ? 'pedido' : 'pedidos'}`
  return n === ps.length ? txt : `${txt} · ${ps.length} volumes`
}

/** id da separação -> "volume i de n", só para pedidos com mais de uma separação, na ordem de `ps`. */
export function numerarVolumes(ps: Pedido[]): Map<string, string> {
  const total = new Map<string, number>()
  for (const p of ps) total.set(chavePedido(p), (total.get(chavePedido(p)) ?? 0) + 1)
  const vistos = new Map<string, number>()
  const out = new Map<string, string>()
  for (const p of ps) {
    const k = chavePedido(p)
    const n = total.get(k) ?? 1
    if (n < 2) continue
    const i = (vistos.get(k) ?? 0) + 1
    vistos.set(k, i)
    out.set(p.id, `volume ${i} de ${n}`)
  }
  return out
}

/** Quantos grupos de filtro estão ativos (de/ate contam como um só). */
export function contaFiltros(f: Filtro): number {
  return [f.canal, f.faixa, f.situacao, f.statusMk, f.embalador, f.alerta].filter(s => s.size > 0).length
    + (f.de || f.ate ? 1 : 0)
}

// ---------- rótulos e formatação da tela ----------
/** Ordem das situações no filtro: aguardando liberação → a separar → em separação → separado → embalado. */
export const ORDEM_SITUACAO: Situacao[] = [0, 1, 4, 2, 3]
export const ROTULO_SITUACAO: Record<Situacao, string> = {
  0: 'Aguardando liberação', 1: 'A separar', 4: 'Em separação', 2: 'Separado', 3: 'Embalado',
}
/** Mesmas cores do modelo aprovado e da TV (a TV usa os tons claros). Aguardando liberação em violeta. */
export const COR_SITUACAO: Record<Situacao, string> = { 0: '#8b5cf6', 1: '#ef4444', 4: '#f59e0b', 2: '#3b82f6', 3: '#10b981' }

/** Aviso fixo da Shopee Decora: o painel não vê a separação dela. */
export const AVISO_DECORA = 'separação no UpSeller — conferir lá'

/** Etapa da tela = situação do Tiny, com o embalado que o marketplace já deu como enviado (coletado) separado em
 * "Enviado". "Embalado" fica só com quem aguarda a coleta. Os dois são embalados: `faltam` não muda.
 * Situação 0 = "Aguardando liberação" (ML retido), antes de "A separar". A Shopee Decora segue a etapa do UpSeller:
 * "A processar no UpSeller", "Etiqueta gerada" (não é embalado) e "Enviado". Na cancelada, `etapa()` diz onde ela
 * estava (rótulo da gaveta), mas ela não conta em nenhum card/barra (ver doEtapa); aparece nos cancelados como
 * "conferir no UpSeller". */
export type Etapa = Situacao | 'enviado' | 'up_a_processar' | 'up_etiqueta'
export const ORDEM_ETAPA: Etapa[] = [0, 1, 4, 2, 'up_a_processar', 'up_etiqueta', 3, 'enviado']
/** Etapas que só aparecem com pedido nelas (flags desligadas = painel igual ao de antes). */
export const ETAPAS_OPCIONAIS: Etapa[] = [0, 'up_a_processar', 'up_etiqueta']
export const ROTULO_ETAPA: Record<Etapa, string> = {
  ...ROTULO_SITUACAO, enviado: 'Enviado', up_a_processar: 'A processar no UpSeller', up_etiqueta: 'Etiqueta gerada',
}
/** Enviado em cinza-azulado (sai do verde do "Embalado"; a TV usa o tom claro #94a3b8). Decora: rosa e ciano. */
export const COR_ETAPA: Record<Etapa, string> = { ...COR_SITUACAO, enviado: '#64748b', up_a_processar: '#ec4899', up_etiqueta: '#0891b2' }
export function etapa(p: Pedido): Etapa {
  if (p.etapa_upseller) {
    if (p.etapa_upseller === 'enviado') return 'enviado'
    if (p.etapa_upseller === 'etiqueta') return 'up_etiqueta'
    if (p.etapa_upseller === 'cancelado') return p.etapa_upseller_antes === 'etiqueta' ? 'up_etiqueta' : 'up_a_processar'
    return 'up_a_processar'
  }
  return p.situacao === 3 && p.status_mk === 'enviado' ? 'enviado' : p.situacao
}
/** Pedidos da etapa, para cards e barras. A Shopee Decora cancelada fica fora de todas as etapas (já está fora do
 * faltam e aparece nos cancelados e na gaveta, onde `etapa()` ainda mostra onde ela estava). */
export const doEtapa = (ps: Pedido[], e: Etapa): Pedido[] =>
  ps.filter(p => p.etapa_upseller !== 'cancelado' && etapa(p) === e)
/** Etapas da tela na ordem: as de sempre + as opcionais que têm pedido em `ps`. Passe a lista inteira (não a
 * filtrada), para os cards não mudarem de lugar com os filtros. */
export function etapasVisiveis(ps: Pedido[]): Etapa[] {
  const tem = new Set<Etapa>(ps.map(etapa))
  if (ps.some(p => p.etapa_upseller)) tem.add('up_a_processar').add('up_etiqueta') // Decora: as duas etapas juntas
  return ORDEM_ETAPA.filter(e => !ETAPAS_OPCIONAIS.includes(e) || tem.has(e))
}

/** Chips de filtro que só existem com dado (flags desligadas = filtros de antes): canal Shopee Decora, situação
 * "Aguardando liberação" e alerta "Aguardando liberação com prazo hoje". Aparecem quando há pedido em `ps` (a lista
 * inteira) ou quando já estão marcados em `f` (para poder desmarcar). */
export function filtrosOpcionais(ps: Pedido[], f: Filtro): { decora: boolean; aguardando: boolean; alertaAguardando: boolean } {
  return {
    decora: f.canal.has('shopee_decora') || ps.some(p => p.canal === 'shopee_decora'),
    aguardando: f.situacao.has(0) || ps.some(p => p.situacao === 0),
    alertaAguardando: f.alerta.has('aguardando_liberacao_hoje') || ps.some(p => p.alertas.includes('aguardando_liberacao_hoje')),
  }
}

/** Marca curta ao lado da etapa: o aviso da Decora, ou o retido que o ML já liberou e o Tiny ainda não separou. */
export function tagPedido(p: Pedido): string | null {
  if (p.etapa_upseller === 'cancelado') return `cancelada · ${AVISO_DECORA}` // ainda aparece em "Etiqueta gerada"
  if (p.canal === 'shopee_decora' || p.etapa_upseller) return AVISO_DECORA
  if (p.situacao === 0 && p.liberado_ml) return 'liberado no ML, aguardando Tiny'
  return null
}

export const ROTULO_FAIXA: Record<Faixa, string> = {
  atrasado: 'Atrasados', hoje: 'Hoje', amanha: 'Amanhã', proximos: 'Próximos dias', sem_prazo: 'Sem prazo', passado: 'Anteriores',
}
export const ROTULO_ALERTA: Record<Alerta, string> = {
  atrasado: 'Atrasado', cancelado_mk: 'Cancelado no marketplace', travado: 'Travado em separação', sem_prazo: 'Sem prazo',
  parado_tiny: 'Separação parada no Tiny', aguardando_liberacao_hoje: 'Aguardando liberação com prazo hoje',
}
export const ROTULO_STATUS_MK: Record<string, string> = {
  a_enviar: 'A enviar', cancelado: 'Cancelado', enviado: 'Já enviado', [SEM_STATUS_MK]: 'Sem status',
}

/** Rótulo do status no marketplace. Amazon Onsite "Shipped" chega como bruto "Manifestado" (a_enviar): o pacote
 * foi manifestado no onsite.amazon.com.br, mas ainda espera a coleta. */
export const rotuloStatusMk = (p: Pedido): string =>
  p.status_mk_bruto === 'Manifestado' ? 'Manifestado' : ROTULO_STATUS_MK[p.status_mk ?? SEM_STATUS_MK]

const doisDig = (n: number) => String(n).padStart(2, '0')
/** Instante UTC ISO → "dd/mm HH:mm" em São Paulo (UTC-3). */
export function dataHoraSP(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(new Date(iso).getTime() - 3 * 3600_000)
  return `${doisDig(d.getUTCDate())}/${doisDig(d.getUTCMonth() + 1)} ${doisDig(d.getUTCHours())}:${doisDig(d.getUTCMinutes())}`
}
/** Instante UTC ISO → "HH:mm" em São Paulo. */
export const horaSP = (iso: string | null): string => dataHoraSP(iso).slice(6)
/** "YYYY-MM-DD" → "dd/mm". */
export const diaMes = (dia: string): string => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`

/** Link do pedido no marketplace (só ML por enquanto). */
export function linkMarketplace(p: Pedido): string | null {
  if (p.canal !== 'ml' && p.canal !== 'ml_flex') return null
  const id = p.ml?.order_id || p.pedido_marketplace
  return id ? `https://www.mercadolivre.com.br/vendas/${id}/detalhe` : null
}

/** Colunas da gaveta e da planilha (mesma ordem). */
export const COLUNAS_LISTA = ['Nota', 'Pedido marketplace', 'Canal', 'Destinatário', 'Forma de envio', 'Envio', 'Situação',
  'Status no marketplace', 'Data máxima', 'Embalador', 'Alertas']

/** Base do ranking "embalados hoje por pessoa": os filtros da tela menos a data máxima (faixa e de/até), para quem
 * embalou hoje um pedido adiantado também contar. O dia (data_checkout = hoje) é aplicado no agrupamento. */
export function baseRanking(ps: Pedido[], f: Filtro): Pedido[] {
  const semData: Filtro = { ...f, faixa: new Set(), de: undefined, ate: undefined }
  return ps.filter(p => passa(p, semData))
}

/** Base da caixa "Pontos de atenção": os filtros da tela menos a data máxima (faixa e de/até) e menos o próprio
 * filtro de alerta. Sem isso, o filtro inicial (Atrasados + Hoje) deixaria "Sem prazo" sempre em 0. */
export function baseAtencao(ps: Pedido[], f: Filtro): Pedido[] {
  const semDataNemAlerta: Filtro = { ...f, faixa: new Set(), alerta: new Set(), de: undefined, ate: undefined }
  return ps.filter(p => passa(p, semDataNemAlerta))
}

/** Pontos do gráfico "andamento hora a hora", de `inicio` a `fim` (horas cheias). O ponto "Hh" é o acumulado até
 * H:00 (o serviço manda assim em `acumulado_hora`, partindo de 0). O ideal é a reta de 0 no início até `total` em
 * `fimIdeal` (a última coleta com pendência; padrão = `fim`) e fica em `total` daí até o fim. */
export function linhasAndamento(acumulado: { hora: string; embalados: number }[], inicio: string, fim: string,
  total: number, fimIdeal: string = fim): { hora: string; ideal: number; real: number | undefined }[] {
  const horas = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h + m / 60 }
  const h0 = Math.floor(horas(inicio)), hf = Math.ceil(horas(fim))
  const vao = Math.max(horas(fimIdeal) - h0, 1 / 60)
  const real = new Map(acumulado.map(x => [Number(x.hora), x.embalados]))
  return Array.from({ length: hf - h0 + 1 }, (_, i) => ({
    hora: `${h0 + i}h`, ideal: Math.round(total * Math.min(1, i / vao)), real: real.get(h0 + i),
  }))
}

// ---------- F2: coletas, cancelados e sem data de envio ----------
const ordemCanal = (c: string) => { const i = CANAIS.indexOf(c as Canal); return i < 0 ? 99 : i }
const rotuloCanal = (c: string) => ROTULO_CANAL[c as Canal] ?? c

const ordemColeta = (c: string) => { const i = ORDEM_COLETAS.indexOf(c as ChaveColeta); return i < 0 ? 99 : i }
/** Rótulo de uma chave de coleta ("Amazon DBA", "Site TM Logística", ou o do canal). */
export const rotuloColeta = (c: string): string => ROTULO_COLETA[c as ChaveColeta] ?? rotuloCanal(c)

/** "Por coleta": hora crescente; mesma hora na ordem das coletas do serviço (DBA logo depois de Amazon, TM de Site). */
export function ordenarColetas(ls: LinhaColeta[]): LinhaColeta[] {
  return [...ls].sort((a, b) => (a.hora === b.hora ? ordemColeta(a.canal) - ordemColeta(b.canal) : a.hora < b.hora ? -1 : 1))
}

/** Pedidos de uma linha de "Por coleta" (`chave` = LinhaColeta.canal). Igual ao serviço (regras.coleta_de): a coleta
 * do pedido (`coleta`; serviço antigo = canal); coleta sem linha na tabela de hoje (`chaves`) cai no canal. */
export function pedidosDaColeta(ps: Pedido[], chave: string, chaves: Set<string>): Pedido[] {
  return ps.filter((p) => {
    const canal = p.canal || 'outro'
    const k = p.coleta ?? canal
    return (chaves.has(k) ? k : canal) === chave
  })
}

/** Lista de "sem data de envio (pendentes)" de um canal: não embalados do canal fora de `canais_com_prazo`, menos
 * os parados no Tiny (o serviço também os tira de sem_prazo_por_canal; eles ficam só em "Separação parada"). */
export function semPrazoDoCanal(ps: Pedido[], canal: string, comPrazo: Set<string>): Pedido[] {
  return ps.filter((p) => {
    const c = p.canal || 'outro'
    return c === canal && !fechado(p) && !comPrazo.has(c) && !p.alertas.includes('parado_tiny')
  })
}

/** "Separação parada no Tiny": lista da equipe inteira (`equipe` = dados.parados_tiny, sem os filtros, como o
 * "retirar da carga"). Sem `equipe` (serviço antigo), cai na base da Atenção. */
export function linhaParados(base: Pedido[], equipe?: Pedido[] | null): Pedido[] {
  return equipe ?? base.filter((p) => p.alertas.includes('parado_tiny'))
}

/** Pedidos de uma caixa de "Pontos de atenção". O parado no Tiny também vem com sem_prazo, mas aparece só em
 * "Separação parada no Tiny" (não conta duas vezes). */
export function atencaoDoAlerta(ps: Pedido[], alerta: Alerta): Pedido[] {
  return ps.filter((p) => p.alertas.includes(alerta) && (alerta !== 'sem_prazo' || !p.alertas.includes('parado_tiny')))
}

/** Ainda sem ritmo: ninguém embalou na última hora e não passou 1 h do início do expediente. `agoraSp` = ISO com
 * o fuso de São Paulo (o `agora_sp` do serviço, "2026-10-22T08:30:00-03:00"); `inicio` = "HH:MM". */
export function semRitmoAinda(ritmoHora: number, agoraSp: string, inicio: string): boolean {
  if (ritmoHora > 0) return false
  const [h, m] = agoraSp.slice(11, 16).split(':').map(Number)
  const [hi, mi] = inicio.split(':').map(Number)
  return h * 60 + m < hi * 60 + mi + 60
}

/** Situação de uma coleta contra o ritmo atual (embalados na última hora). Coleta que passou nunca mostra
 * "precisa de": só quantos ficaram. `semRitmo` (ver semRitmoAinda): no começo do dia, sem ninguém embalar ainda,
 * a linha fica neutra ("sem ritmo ainda") em vez de vermelha. */
export function statusColeta(c: LinhaColeta, ritmoHora: number, semRitmo = false): { texto: string; tom: 'ok' | 'erro' | 'neutro' } {
  if (c.passou) return c.ficaram > 0 ? { texto: `coleta passou: ${c.ficaram} ficaram`, tom: 'erro' } : { texto: 'coleta passou: tudo embalado', tom: 'ok' }
  if (c.faltam === 0) return { texto: 'tudo embalado', tom: 'ok' }
  if ((c.precisa_hora ?? Infinity) <= ritmoHora) return { texto: 'no ritmo', tom: 'ok' }
  return semRitmo ? { texto: 'sem ritmo ainda', tom: 'neutro' } : { texto: 'não dá', tom: 'erro' }
}

export type AvisoDia = { tom: 'info' | 'warning' | 'success' | 'error'; texto: string; secundario?: string }

/** Aviso ao lado do ritmo. Coleta em vermelho na tabela (passou com pedido ou não fecha no ritmo atual) manda no
 * aviso e é citada pelo nome; a previsão contra o fim do expediente fica só como texto secundário (nunca um verde
 * "tudo termina" ao lado de uma coleta vermelha). `coletas` em ordem de hora; `fim` = "HH:MM". */
export function avisoDoDia(d: DiaDeHoje, coletas: LinhaColeta[], fim: string, semRitmo = false): AvisoDia {
  const fimH = fim.replace(':00', 'h')
  if (d.encerrado) return { tom: d.faltam ? 'warning' : 'success', texto: `Expediente encerrado: ficaram ${d.faltam} pedidos.` }
  if (d.faltam === 0) return { tom: 'success', texto: 'Tudo embalado: nada falta para sair hoje.' }
  let geral: AvisoDia
  if (!d.previsao_fim) geral = { tom: 'info', texto: 'Sem ritmo na última hora: ainda não dá para prever o término.' }
  else if (d.ficariam > 0) geral = { tom: 'warning', texto: `No ritmo atual, tudo termina por volta de ${horaSP(d.previsao_fim)}: ~${d.ficariam} pedidos ficariam para depois do fim do expediente (${fimH}).` }
  else geral = { tom: 'success', texto: `No ritmo atual, tudo termina por volta de ${horaSP(d.previsao_fim)}, antes do fim do expediente (${fimH}).` }
  const risco = coletas.filter((c) => statusColeta(c, d.ritmo_hora, semRitmo).tom === 'erro')
  if (!risco.length) return geral
  const c = risco[0]
  const outras = risco.length > 1 ? ` Mais ${risco.length - 1} ${risco.length === 2 ? 'coleta' : 'coletas'} em risco na tabela.` : ''
  const texto = c.passou
    ? `A coleta ${c.rotulo} ${c.hora} passou: ${c.ficaram} ${c.ficaram === 1 ? 'ficou' : 'ficaram'}.${outras}`
    : `No ritmo atual, a coleta ${c.rotulo} ${c.hora} não fecha (faltam ${c.faltam}).${outras}`
  return { tom: 'error', texto, secundario: geral.texto }
}

/** Shopee Decora cancelada depois da etiqueta: não dá para saber se já foi embalada (conferir no UpSeller). */
const decoraCanceladaComEtiqueta = (p: Pedido) =>
  p.canal === 'shopee_decora' && p.etapa_upseller === 'cancelado' && p.etapa_upseller_antes === 'etiqueta'

/** Cancelados no marketplace: embalados (retirar da carga) x ainda não embalados (não separar). A Decora cancelada
 * com etiqueta vai junto dos embalados (como no serviço); Decora cancelada nunca é "não separar" do Tiny. */
export function separarCancelados(ps: Pedido[]): { embalados: Pedido[]; naoEmbalados: Pedido[] } {
  const c = ps.filter((p) => p.alertas.includes('cancelado_mk'))
  return {
    embalados: c.filter((p) => p.situacao === 3 || decoraCanceladaComEtiqueta(p)),
    naoEmbalados: c.filter((p) => p.situacao !== 3 && p.etapa_upseller !== 'cancelado'),
  }
}

/** Caixa dos cancelados da tela: "Embalados — retirar da carga" é da equipe inteira (`equipe` =
 * dados.cancelados_embalados, sem os filtros: pacote cancelado nunca some da carga por causa de um filtro);
 * "Ainda não embalados" segue os filtros (`base` = baseAtencao). Sem `equipe` (serviço antigo), cai na base. */
export function linhasCancelados(base: Pedido[], equipe?: Pedido[] | null): { embalados: Pedido[]; decora: Pedido[]; naoEmbalados: Pedido[] } {
  const r = separarCancelados(base)
  const todos = equipe ?? r.embalados
  // Shopee Decora cancelada com etiqueta: linha própria, "retirar/não separar (conferir no UpSeller)"
  return {
    embalados: todos.filter((p) => p.canal !== 'shopee_decora'),
    decora: todos.filter((p) => p.canal === 'shopee_decora'),
    naoEmbalados: r.naoEmbalados,
  }
}

/** Pendentes dos canais sem prazo de API, maior primeiro (empate na ordem dos canais); zeros fora. */
export function semPrazoPorCanal(m: Record<string, number>): { canal: string; rotulo: string; n: number }[] {
  return Object.entries(m).filter(([, n]) => n > 0)
    .sort(([a, na], [b, nb]) => nb - na || ordemCanal(a) - ordemCanal(b))
    .map(([canal, n]) => ({ canal, rotulo: rotuloCanal(canal), n }))
}

/** "Shopee 65 · Outros 17 · …" ("" quando não há nada). */
export const textoSemPrazo = (l: { rotulo: string; n: number }[]): string => l.map((x) => `${x.rotulo} ${x.n}`).join(' · ')

/** Embalados do dia que ficam fora da curva hora a hora (o `saldo_inicial` do serviço): sem hora observada, embalados
 * em dia anterior ou antes do início do expediente (SP). `dia` = "YYYY-MM-DD"; `inicio` = "HH:MM". */
export function jaEmbaladosAoAbrir(ps: Pedido[], dia: string, inicio: string): Pedido[] {
  const h0 = Number(inicio.split(':')[0])
  return ps.filter((p) => {
    if (!feito(p)) return false
    if (!p.embalado_em) return true // Decora enviada não tem embalado_em: saldo, como no serviço
    const sp = new Date(new Date(p.embalado_em).getTime() - 3 * 3600_000)
    return sp.toISOString().slice(0, 10) < dia || sp.getUTCHours() < h0
  })
}

/** "20 faltam · 18 a separar · 2 separados"; cada parte só aparece se houver. Soma as mesmas chaves do `faltam` do
 * serviço: '0' (aguardando liberação), '1', '4', '2' e, da Decora, 'up_a_processar' e 'up_etiqueta'. */
export function resumoFaltam(porSituacao: Record<string, number>): string {
  const n = (k: string) => porSituacao[k] ?? 0
  const partes = [`${n('0') + n('1') + n('4') + n('2') + n('up_a_processar') + n('up_etiqueta')} faltam`]
  if (n('0')) partes.push(`${n('0')} aguardando liberação`)
  if (n('1')) partes.push(`${n('1')} a separar`)
  if (n('4')) partes.push(`${n('4')} em separação`)
  if (n('2')) partes.push(`${n('2')} ${n('2') === 1 ? 'separado' : 'separados'}`)
  if (n('up_a_processar')) partes.push(`${n('up_a_processar')} a processar no UpSeller`)
  if (n('up_etiqueta')) partes.push(`${n('up_etiqueta')} com etiqueta (UpSeller)`)
  return partes.join(' · ')
}

/** Coluna "Situação" da gaveta e da planilha: a etapa e, se houver, a tag entre parênteses. */
export function situacaoTexto(p: Pedido): string {
  const t = tagPedido(p)
  return t ? `${ROTULO_ETAPA[etapa(p)]} (${t})` : ROTULO_ETAPA[etapa(p)]
}

/** Planilha da gaveta: CSV com BOM e `;`, que o Excel abre direto. */
export function csvPedidos(ps: Pedido[]): string {
  const esc = (v: string | { bruto: string }) => {
    if (typeof v !== 'string') return v.bruto
    // não deixa o Excel interpretar como fórmula (=, +, -, @, tab, CR no início); número negativo puro passa
    const s = /^[=+\-@\t\r]/.test(v) && !/^-\d+([.,]\d+)?$/.test(v) ? `'${v}` : v
    return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  // número longo (pedido ML tem 16 dígitos) vira ="…" para o Excel não arredondar nem pôr em notação científica
  const longo = (v: string | null) => (v && /^\d{12,}$/.test(v) ? { bruto: `="${v}"` } : v ?? '')
  const linhas = ps.map(p => [
    p.nota ?? '', longo(p.pedido_marketplace), p.canal_rotulo, p.destinatario ?? '', p.forma_envio ?? '', longo(p.envio ?? null),
    situacaoTexto(p), rotuloStatusMk(p), dataHoraSP(p.prazo),
    p.embalador_id ? p.embalador_nome : '', p.alertas.map(a => ROTULO_ALERTA[a]).join(', '),
  ])
  return `\ufeff${[COLUNAS_LISTA, ...linhas].map(l => l.map(esc).join(';')).join('\r\n')}`
}
